import React, { useEffect, useState } from "react";
import {
  ChevronLeft,
  Package,
  Clock,
  ShoppingBag,
  Truck,
  ChevronDown,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import { Download, FileEarmarkPdf, GeoAlt } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import {
  getStoreOwnerOrderDetails,
  updateOrderStatus,
} from "../../services/storeOwnerOrderServices";
import { notify } from "../../components/Common/Alerts";
import { formatCurrencyINR } from "../../utils/function";
import { useConfig } from "../../components/Context/ConfigContext";
import { useSelector } from "react-redux";

const OrderDetailsSkeleton = () => {
  return (
    <div className="flex flex-col min-h-screen bg-[#FFFBF7] animate-pulse">
      <div className="bg-[#E67E22] p-4 h-[56px]" />

      <div className="p-4 space-y-4">
        <div className="bg-white rounded-3xl p-5 space-y-4 shadow-sm">
          <div className="h-3 w-24 bg-gray-200 rounded" />
          <div className="h-6 w-32 bg-gray-300 rounded" />

          <div className="grid grid-cols-3 gap-4 pt-4">
            <div className="h-4 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded" />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 space-y-4 shadow-sm">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex justify-between items-center">
              <div className="flex gap-3 items-center">
                <div className="w-10 h-10 bg-gray-200 rounded-xl" />
                <div className="space-y-2">
                  <div className="h-3 w-24 bg-gray-200 rounded" />
                  <div className="h-3 w-16 bg-gray-100 rounded" />
                </div>
              </div>
              <div className="h-4 w-12 bg-gray-200 rounded" />
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl p-4 space-y-3 shadow-sm">
          <div className="flex justify-between">
            <div className="h-3 w-20 bg-gray-200 rounded" />
            <div className="h-3 w-16 bg-gray-300 rounded" />
          </div>
          <div className="flex justify-between">
            <div className="h-3 w-24 bg-gray-200 rounded" />
            <div className="h-3 w-16 bg-gray-300 rounded" />
          </div>
          <div className="flex justify-between">
            <div className="h-4 w-28 bg-gray-300 rounded" />
            <div className="h-4 w-20 bg-gray-400 rounded" />
          </div>
        </div>
      </div>
    </div>
  );
};

const StoreOwnerOrderDetails = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { t } = useTranslation();
  const { configData } = useConfig();
  let auth = useSelector((x) => x.auth.value);

  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusChangeModel, setStatusChangeModel] = useState(false);
  const [statusChangeData, setStatusChangeData] = useState("");
  const [btnLoading, setBtnLoading] = useState(false);

  const fetchOrders = async () => {
    if (uuid) {
      try {
        setLoading(true);

        let data = {
          uuid: uuid,
          store_id: auth.active_store_id,
        };
        const response = await getStoreOwnerOrderDetails(data);
        const decrypted = decryptData(response.data.data);

        setOrderDetails(decrypted.data);
      } catch (err) {
        notify.error(Utils.getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [uuid]);

  if (loading || !orderDetails) {
    return <OrderDetailsSkeleton />;
  }

  const paidAmount = orderDetails.total_amount - orderDetails.pending_amount;

  const handleDownloadInvoice = () => {
    const link = document.createElement("a");
    link.href = orderDetails.invoice_file;
    link.download = "";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenMap = () => {
    if (!orderDetails?.latitude || !orderDetails?.longitude) return;

    const destination = `${orderDetails.latitude},${orderDetails.longitude}`;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const origin = `${position.coords.latitude},${position.coords.longitude}`;

        const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`;

        window.open(url, "_blank");
      },
      () => {
        const url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
        window.open(url, "_blank");
      },
    );
  };

  const handleStatusChange = (order, value) => {
    const updatedOrder = {
      ...order,
      status_value: Number(value),
    };

    setStatusChangeData(updatedOrder);
    setStatusChangeModel(true);
  };

  const getFilteredStatusOptions = (currentStatus) => {
    switch (currentStatus) {
      case 1: // Pending
        return configData?.order_status;

      case 2: // Confirmed
        return configData?.order_status.filter((s) =>
          [2, 3, 4].includes(s.value),
        );

      case 3: // Completed
        return configData?.order_status.filter((s) => s.value === 3);

      case 4: // Cancelled
        return configData?.order_status.filter((s) => s.value === 4);

      default:
        return configData?.order_status;
    }
  };

  const handlSubmitOrderStatus = async (e) => {
    e.preventDefault();

    let data = {
      order_uuid: statusChangeData?.uuid,
      status: statusChangeData?.status_value,
      store_id: auth.active_store_id,
    };
    try {
      const response = await updateOrderStatus(data);
      const decrypted = decryptData(response.data.data);
      notify.success(decrypted.message);
      setStatusChangeData("");
      setStatusChangeModel(false);
      fetchOrders();
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <div className="flex flex-col bg-[#FFFBF7] dark:bg-slate-950 transition-colors duration-300">
      <div
        className="bg-[#E67E22] dark:bg-orange-600 p-4 flex items-center gap-3 text-white shadow-md cursor-pointer hidden md:flex"
        onClick={() => navigate("/all-store-owner-order-list")}
      >
        <ChevronLeft size={24} />
        <h1 className="text-lg font-bold">{t("OrderDetails")}</h1>
      </div>

      <div className="max-w-7xl mx-auto w-full p-4 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-7 space-y-6">
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-5 shadow-sm border border-orange-50 dark:border-slate-800">
              <div className="flex justify-between items-start mb-5 gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-gray-400 dark:text-slate-500 font-black uppercase tracking-widest mb-1">
                    {t("orderId")}
                  </p>
                  <div className="flex items-center gap-2">
                    <h2 className="text-[20px] sm:text-[22px] font-black text-gray-900 dark:text-white leading-none truncate">
                      #{orderDetails.order_number}
                    </h2>

                    {orderDetails?.invoice_file && (
                      <button
                        onClick={handleDownloadInvoice}
                        className="p-1.5 bg-orange-50 dark:bg-slate-800 hover:bg-orange-100 dark:hover:bg-slate-700 rounded-lg transition-all active:scale-90 flex items-center justify-center shrink-0"
                        title="Download Invoice"
                      >
                        <FileEarmarkPdf
                          size={18}
                          className="text-[#E67E22] dark:text-orange-500"
                        />
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 shrink-0">
                  <div className="relative">
                    <select
                      value={orderDetails.status_value}
                      disabled={[3, 4].includes(orderDetails.status_value)} // lock completed & cancelled
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleStatusChange(orderDetails, e.target.value);
                      }}
                      className={`appearance-none px-4 py-1.5 pr-8 rounded-full text-[11px] font-bold outline-none transition-all border-none
                                            ${
                                              orderDetails.status_value === 3
                                                ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600"
                                                : orderDetails.status_value ===
                                                    4
                                                  ? "bg-red-50 dark:bg-red-900/20 text-red-600"
                                                  : "bg-orange-50 dark:bg-orange-900/20 text-orange-600"
                                            }
                                           ${[3, 4].includes(orderDetails.status_value) ? "cursor-not-allowed opacity-70" : "cursor-pointer"}
                                             `}
                    >
                      {getFilteredStatusOptions(orderDetails.status_value).map(
                        (status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ),
                      )}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <ChevronDown
                        size={10}
                        className={
                          orderDetails.status_value === 3
                            ? "text-emerald-600"
                            : "text-orange-600"
                        }
                      />
                    </div>
                  </div>

                  <div
                    className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tight ${
                      orderDetails.payment_type_value === 3
                        ? "bg-green-50 dark:bg-emerald-900/20 text-green-600 dark:text-emerald-400"
                        : orderDetails.payment_type_value === 2
                          ? "bg-orange-50 dark:bg-orange-900/20 text-orange-600"
                          : "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400"
                    }`}
                  >
                    {orderDetails.payment_type_label}
                  </div>
                </div>
              </div>

              <div className="flex items-center border-l-2 border-orange-200 dark:border-slate-700 gap-2 pl-3">
                <Clock size={16} className="text-orange-400" />
                <span className="text-[12px] font-bold text-gray-500 dark:text-slate-400">
                  {orderDetails.created_at}
                </span>
              </div>

              <div className="flex items-center gap-2 border-l-2 border-orange-200 dark:border-slate-700 pl-3 mt-3">
                {Number(orderDetails.order_type_value) === 2 ? (
                  <Truck size={16} className="text-orange-400" />
                ) : (
                  <ShoppingBag size={16} className="text-orange-400" />
                )}
                <span className="text-[12px] font-bold text-gray-500 dark:text-slate-400">
                  {orderDetails.order_type_label}
                </span>
              </div>

              {orderDetails.delivery_address && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenMap();
                  }}
                  className="flex items-start gap-2 border-l-2 border-orange-200 dark:border-slate-700 pl-3 mt-3 cursor-pointer"
                >
                  <GeoAlt size={20} className="text-orange-400 mt-0.5" />
                  <span className="text-[11px] text-gray-400 dark:text-slate-500">
                    {orderDetails.delivery_address}
                  </span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {orderDetails?.order_history?.length > 0 && (
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-slate-800">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                      <span className="w-1 h-6 bg-orange-500 rounded-full mr-3"></span>
                      {t("orderActivity")}
                    </h2>

                    <div className="relative pl-8 space-y-8 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-100 dark:before:bg-slate-800">
                      {orderDetails?.order_history?.map((items, index) => (
                        <div key={index} className="relative">
                          <div className="absolute -left-[27px] top-1.5 h-4 w-4 rounded-full border-4 border-white dark:border-slate-900 bg-emerald-500 shadow-sm"></div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                              {items.status_label}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                              {items.created_at}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {orderDetails?.payment_history?.length > 0 && (
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-slate-800">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                      <span className="w-1 h-6 bg-orange-500 rounded-full mr-3"></span>
                      {t("paymentSummary")}
                    </h2>

                    <div className="relative pl-8 space-y-8 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-0.5 before:bg-gray-100 dark:before:bg-slate-800">
                      {orderDetails?.payment_history?.map((payment, index) => (
                        <div key={index} className="relative">
                          <div className="absolute -left-[27px] top-1.5 h-4 w-4 rounded-full border-4 border-white dark:border-slate-900 bg-emerald-500 shadow-sm"></div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                              {formatCurrencyINR(payment.amount)} (
                              {payment.payment_method_label})
                            </p>
                            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                              {payment.created_at}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-8">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 overflow-hidden">
              <div className="px-4 py-3 bg-orange-50 dark:bg-slate-800">
                <p className="text-[10px] font-black text-orange-800 dark:text-orange-400 uppercase">
                  {t("orderedItems")} ({orderDetails?.finalItems?.length})
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 divide-y md:divide-y-0 lg:divide-y dark:divide-slate-800">
                {orderDetails?.finalItems?.map((item, index) => (
                  <div
                    key={index}
                    className="p-4 flex justify-between border-b dark:border-slate-800 last:border-0"
                  >
                    <div className="flex gap-3">
                      {/* <Package size={20} className="text-gray-400" /> */}
                      <div className="relative shrink-0">
                        <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800/50 rounded-2xl flex items-center justify-center border border-slate-100 dark:border-slate-800 shadow-sm">
                          <span className="text-3xl">{item?.icon || "📦"}</span>
                        </div>

                        {/* <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 px-2 py-1 rounded-lg shadow-md flex flex-col items-center">
                          {item.price && item.price < item.selling_price && (
                            <span className="text-[7px] text-zinc-400 line-through leading-none mb-0.5">
                              {formatCurrencyINR(item.selling_price)}
                            </span>
                          )}
                          <p className="text-[10px] font-black text-orange-600 whitespace-nowrap leading-none">
                            {formatCurrencyINR(
                              item.price ?? item.selling_price,
                            )}
                          </p>
                        </div> */}
                      </div>
                      <div className="flex-1 flex flex-col gap-1 pt-0.5">
                        <div className="flex justify-between items-start">
                          <h4 className="font-black text-slate-800 dark:text-white text-base leading-tight">
                            {item.name} ({item.unit_qty}
                            {item?.unit})
                          </h4>
                        </div>

                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">
                            {t("quantity")}
                          </span>
                          <p className="text-[11px] text-zinc-500 dark:text-zinc-400 font-medium">
                            {item?.quantity} {item?.unit_name}
                          </p>
                        </div>

                        <div className="mt-2 flex items-center justify-between border-t border-slate-50 dark:border-slate-800">
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-[10px] text-zinc-400 uppercase font-black">
                              {t("total")}:
                            </span>
                            <div className="flex flex-col">
                              {item.total_price &&
                                item.total_price <
                                  item.total_original_price && (
                                  <span className="text-[9px] text-zinc-400 line-through leading-none">
                                    {formatCurrencyINR(
                                      item.total_original_price,
                                    )}
                                  </span>
                                )}
                              <span className="text-base font-black text-slate-900 dark:text-orange-500 leading-none">
                                {formatCurrencyINR(item.total_price)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 space-y-3 shadow-sm border dark:border-slate-800">
              <div className="flex justify-between text-sm dark:text-slate-300">
                <span>{t("paidAmount")}</span>
                <span className="text-green-600 dark:text-emerald-400 font-medium">
                  {formatCurrencyINR(paidAmount)}
                </span>
              </div>

              <div className="flex justify-between text-sm dark:text-slate-300">
                <span>{t("pendingAmount")}</span>
                <span className="text-red-500 dark:text-red-400 font-medium">
                  {formatCurrencyINR(orderDetails.pending_amount)}
                </span>
              </div>

              <div className="flex justify-between pt-3 border-t dark:border-slate-800">
                <span className="font-bold dark:text-white">
                  {t("subtotal")}
                </span>
                <span className="text-orange-600 dark:text-orange-500 font-black text-lg">
                  {formatCurrencyINR(orderDetails.total_amount)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {statusChangeModel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#161e2d] rounded-3xl p-6 w-[90%] max-w-sm shadow-2xl animate-fade-in">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-2">
              {t("confirmOrderStatus")}?
            </h2>

            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              {t("confirmOrderStatusDescription")}
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setStatusChangeModel(false)}
                className="px-4 py-2 rounded-xl bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 font-semibold hover:bg-gray-200 transition"
              >
                {t("cancel")}
              </button>

              <button
                onClick={handlSubmitOrderStatus}
                disabled={btnLoading}
                className={`px-4 py-2 rounded-xl font-semibold transition flex items-center justify-center gap-2
    ${
      btnLoading
        ? "bg-red-400 cursor-not-allowed"
        : "bg-red-500 hover:bg-red-600 text-white"
    }`}
              >
                {btnLoading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    {t("processing")}
                  </>
                ) : (
                  t("statusChange")
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreOwnerOrderDetails;
